import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
export const AUTH_TOKEN_COOKIE_NAME = "pylo-auth-token";
export const REFRESH_TOKEN_COOKIE_NAME = "pylo-refresh-token";
export function cn(...inputs) {
    return twMerge(clsx(inputs));
}
// disabled fields for udpate / create entity instance
export const disabledFields = ["id", "created_at", "updated_at", "integer_id"];
export const paginationDataStr = `pagination {
  current_page
  has_more_pages
  last_page
  total
  per_page
}
`;
export const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};
export const removeKeysFromObject = (obj, keysToRemove) => {
    return Object.fromEntries(Object.entries(obj).filter(([key, value]) => {
        return !keysToRemove.includes(key) && value !== null;
    }));
};
export const isGraphQlErrorReponse = (error) => {
    if (error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "status" in error.response) {
        return true;
    }
};
export const ensureFirstLetterLowercase = (str) => {
    if (!str || typeof str !== "string") {
        throw new Error("Input must be a non-empty string");
    }
    return str.charAt(0).toLowerCase() + str.slice(1);
};
export const cleanupEntityName = (entityName) => {
    if (entityName.endsWith("ListData")) {
        return entityName.replace("ListData", "");
    }
    else if (entityName.endsWith("Data")) {
        return entityName.replace("Data", "");
    }
    else
        return entityName;
};
export const isUUID = (content) => {
    const regex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return regex.test(content);
};
export const splitByFilter = (array, predicate) => {
    return array.reduce((acc, item) => {
        if (predicate(item)) {
            acc[0].push(item);
        }
        else {
            acc[1].push(item);
        }
        return acc;
    }, [[], []]);
};
export const cleanPhone = (value) => {
    return value.toString().replace(/[\s\-\(\)\.\/]/g, "");
};
export const VALIDATION_REGEX = {
    EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    PHONE: /^(?:(?:\+|00)[1-9][0-9]{0,2}|0)[0-9\s\/()-]{4,}$/,
    WEBSITE: /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,255}(\.[a-zA-Z0-9()]{1,6})?(:[0-9]{1,5})?([-a-zA-Z0-9()@:%_\+.~#?&\/\/=]*)$/,
};
export const buildValidationRegexString = (type, isRequired = false) => {
    const isRequiredString = isRequired ? "required" : "nullable";
    const regex = VALIDATION_REGEX[type];
    return `regex:/${regex.source}/;${isRequiredString}`;
};
export const validateEmail = (email) => {
    return VALIDATION_REGEX.EMAIL.test(email);
};
export const validatePhone = (phone) => {
    return VALIDATION_REGEX.PHONE.test(phone);
};
export const validateWebsite = (url) => {
    return VALIDATION_REGEX.WEBSITE.test(url);
};
// Helper function to parse validation errors from GraphQL response
export const parseValidationErrors = (error) => {
    try {
        const errors = error?.response?.errors;
        if (!Array.isArray(errors) || errors.length === 0)
            return null;
        const firstError = errors[0];
        // Check for validation errors in extensions
        const validationErrors = firstError?.extensions?.validationErrors;
        if (validationErrors?.length > 0) {
            const firstValidation = validationErrors[0];
            if (firstValidation.errors?.length > 0) {
                return firstValidation.errors[0].message;
            }
        }
        // Fallback to top-level error message
        return firstError?.message ?? null;
    }
    catch (e) {
        console.error("Error parsing validation errors:", e);
        return null;
    }
};
// Build nested GraphQL selection strings from dotted field paths.
// For example:
// [
//   'title',
//   'T2_variants.value',
//   'T2_variants.variant',
//   'T2_variants.meta.author.name'
// ]
// ->
// title\nT2_variants {\n  data {\n    value\n    variant\n    meta {\n      data {\n        author {\n          data {\n            name\n          }\n        }\n      }\n    }\n  }\n}
export const buildNestedDataFieldsForRelations = (fields) => {
    if (!fields || fields.length === 0)
        return "";
    const simpleFields = new Set();
    const relationToSubpaths = new Map();
    for (const field of fields) {
        if (!field.includes(".")) {
            simpleFields.add(field);
            continue;
        }
        const [relation, ...rest] = field.split(".");
        if (rest.length === 0) {
            simpleFields.add(relation);
            continue;
        }
        const existing = relationToSubpaths.get(relation) ?? new Set();
        existing.add(rest.join("."));
        relationToSubpaths.set(relation, existing);
    }
    const buildNested = (parts) => {
        if (parts.length === 0)
            return "";
        if (parts.length === 1)
            return parts[0];
        const [head, ...rest] = parts;
        return `${head} {\n        data {\n          ${buildNested(rest)}\n        }\n      }`;
    };
    const relationSelections = Array.from(relationToSubpaths.entries()).map(([relation, subpaths]) => {
        const nested = Array.from(subpaths)
            .map((sf) => buildNested(sf.split(".")))
            .join("\n");
        return `${relation} {\n        data {\n          ${nested}\n        }\n      }`;
    });
    return [...Array.from(simpleFields), ...relationSelections].join("\n");
};
export const ImageMimeTypes = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/avif",
    "image/x-icon",
    "image/gif",
    "image/bmp",
];
export const parseValidationString = (validationString) => {
    if (!validationString) {
        return {
            required: false,
            min: undefined,
            max: undefined,
            regex: undefined,
            regexValidationErrorMessage: undefined,
        };
    }
    const validations = validationString.split(";");
    const minRule = validations.find((v) => v.startsWith("min:"));
    const maxRule = validations.find((v) => v.startsWith("max:"));
    const regexRule = validations.find((v) => v.startsWith("regex:"));
    const bla = {
        required: validations.some((v) => v === "required"),
        min: minRule ? parseInt(minRule.split(":")[1] ?? "0") : undefined,
        max: maxRule ? parseInt(maxRule.split(":")[1] ?? "0") : undefined,
        regex: regexRule ? regexRule.split(":")[1] : undefined,
        regexValidationErrorMessage: regexRule ? regexRule.split(":")[2]?.slice(1, -1) : undefined,
    };
    return bla;
};
export const buildValidationString = ({ isRequired, min = undefined, max = undefined, regex = undefined, regexValidationErrorMessage = undefined, }) => {
    const validations = [];
    if (isRequired) {
        validations.push("required");
    }
    if (min) {
        validations.push(`min:${min}`);
    }
    if (max) {
        validations.push(`max:${max}`);
    }
    if (regex && !regexValidationErrorMessage) {
        validations.push(`regex:${regex}`);
    }
    if (regex && regexValidationErrorMessage) {
        validations.push(`regex:${regex}:'${regexValidationErrorMessage}'`);
    }
    return validations.join(";").trim();
};
export function buildFileAcceptConfig(entityRelation) {
    const { media_allowed_computed_mime_types: mimeTypes = [], media_allowed_computed_extensions: extensions = [], } = entityRelation || {};
    // If both arrays are empty or undefined → accept everything
    if (!mimeTypes.length && !extensions.length) {
        return undefined;
    }
    // If octet-stream (binary) is allowed → treat as a wildcard and accept everything
    if (mimeTypes.includes("application/octet-stream")) {
        return undefined;
    }
    // Convert extensions -> .ext format
    const dotExtensions = extensions.map((ext) => (ext.startsWith(".") ? ext : `.${ext}`));
    // Build accept object
    const accept = {};
    mimeTypes.forEach((type) => {
        // Associate all allowed extensions with this MIME type
        accept[type] = dotExtensions.length ? dotExtensions : [];
    });
    return accept;
}
